import { useMemo } from "react"
import { AlertTriangle, ArrowLeftRight, Lock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type {
  CouplageMatrix,
  CouplagePlanColumn,
} from "@/features/milling/lib/couplage-validation"
import { sourceKey } from "@/features/milling/lib/couplage-validation"
import type { CouplingLoadView } from "@/features/milling/api/types"
import { formatNumber, kgToMt } from "@/lib/format"

interface ConfirmCouplageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  loads: CouplingLoadView[]
  columns: CouplagePlanColumn[]
  matrix: CouplageMatrix
  onConfirm: () => void
  isPending: boolean
}

interface SourceRemainder {
  key: string
  label: string
  kind: "LOAD" | "QUEUE_LOT"
  remainderBags: number
  remainderKg: number
}

// ConfirmCouplageDialog is the gate between PlanningView and the
// final SubmitCouplage call. It shows the operator exactly what is
// about to happen:
//   1. The plan locks — no more matrix edits, no chargement adds.
//   2. Bags routed into the matrix become committed to the plan.
//   3. Bags that the operator chose NOT to route return to Magasin
//      as Disponible immediately (per-source breakdown).
//
// The dialog is read-only context + a Confirm/Cancel pair. Save
// draft does NOT go through this — only the final submit.
export function ConfirmCouplageDialog({
  open,
  onOpenChange,
  loads,
  columns,
  matrix,
  onConfirm,
  isPending,
}: ConfirmCouplageDialogProps) {
  const { t } = useTranslation("milling")

  const { totalCommittedBags, totalCommittedKg, remainders } = useMemo(() => {
    const remainderRows: SourceRemainder[] = []
    let committedBags = 0
    let committedKg = 0

    for (const l of loads) {
      const key = sourceKey(l.sourceKind, l.loadId)
      const routed = Object.values(matrix[key] ?? {}).reduce(
        (a, v) => a + (Number(v) || 0),
        0,
      )
      const remainder = Math.max(0, (l.bagsAssigned ?? 0) - routed)
      committedBags += routed
      committedKg += routed * (l.avgBagKg ?? 0)
      if (remainder > 0) {
        remainderRows.push({
          key,
          label:
            l.sourceKind === "QUEUE_LOT"
              ? (l.loadNumber ?? `QDL #${l.loadId}`)
              : (l.cooperativeName ??
                l.loadNumber ??
                `Chargement #${l.loadId}`),
          kind: l.sourceKind,
          remainderBags: remainder,
          remainderKg: remainder * (l.avgBagKg ?? 0),
        })
      }
    }
    return {
      totalCommittedBags: committedBags,
      totalCommittedKg: committedKg,
      remainders: remainderRows,
    }
  }, [loads, matrix])

  const totalRemainderBags = remainders.reduce(
    (a, r) => a + r.remainderBags,
    0,
  )
  const totalRemainderKg = remainders.reduce((a, r) => a + r.remainderKg, 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("confirmCouplage.title")}</DialogTitle>
          <DialogDescription>
            {t("confirmCouplage.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm">
          {/* Committed summary */}
          <section className="rounded-md border bg-muted/30 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t("confirmCouplage.committedHeading")}
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-3 tabular-nums">
              <div>
                <span className="text-base font-semibold">
                  {formatNumber(totalCommittedBags)}
                </span>{" "}
                <span className="text-xs text-muted-foreground">
                  {t("confirmCouplage.bagsAcross", {
                    count: columns.length,
                  })}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                ≈ {formatNumber(kgToMt(totalCommittedKg), 3)}{" "}
                {t("confirmCouplage.mt")}
              </div>
            </div>
          </section>

          {/* Remainders returning */}
          {totalRemainderBags > 0 ? (
            <section className="rounded-md border border-border bg-card px-3 py-2">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <ArrowLeftRight className="size-3.5" />
                {t("confirmCouplage.returningHeading", {
                  bags: formatNumber(totalRemainderBags),
                  mt: formatNumber(kgToMt(totalRemainderKg), 3),
                })}
              </div>
              <ul className="mt-2 flex flex-col gap-1">
                {remainders.map((r) => (
                  <li
                    key={r.key}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="truncate">{r.label}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {formatNumber(r.remainderBags)}{" "}
                      {t("confirmCouplage.bagsShort")} ·{" "}
                      {formatNumber(kgToMt(r.remainderKg), 3)}{" "}
                      {t("confirmCouplage.mt")}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <section className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              {t("confirmCouplage.noRemainder")}
            </section>
          )}

          {/* Lock warning */}
          <section className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
            <Lock className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
            <div className="text-foreground/80">
              {t("confirmCouplage.lockWarning")}
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("confirmCouplage.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <AlertTriangle className="size-4 animate-pulse" />
                {t("confirmCouplage.submitting")}
              </>
            ) : (
              t("confirmCouplage.confirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
